import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  ArrowsLeftRight,
  MagnifyingGlass as Search,
  DotsSixVertical,
  CalendarBlank,
  CaretRight,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useMattersStore, PIPELINE_STAGES, type Matter } from '@/lib/mattersStore';
import { useAuthStore } from '@/features/auth/store';
import { useClerkWorkspace } from '@/lib/useClerkWorkspace';
import { canSeeMatter, type PrincipalLite } from '@/lib/permissions';
import { canCreateMatter, canUpdateMatter } from '@/lib/pupilUiBoundary';
import { useChambersMode, SOLO_PIPELINE_STAGES, CHAMBERS_PIPELINE_STAGES } from '@/lib/useChambersMode';
import { onMattersUpdate } from '@/lib/websocket';
import { useQuery } from '@tanstack/react-query';
import { fetchClerkOperations, fetchMatterHealth } from '@/lib/reportsApi';

const STAGE_STYLES: Record<string, { color: string; lightBg: string; border: string }> = {
  active:            { color: 'bg-emerald-500', lightBg: 'bg-emerald-50 dark:bg-emerald-500/10', border: 'border-emerald-200 dark:border-emerald-500/30' },
  listed:            { color: 'bg-brand-500',   lightBg: 'bg-brand-50',   border: 'border-brand-200'   },
  in_trial:          { color: 'bg-amber-500',   lightBg: 'bg-amber-50 dark:bg-amber-500/10',   border: 'border-amber-200 dark:border-amber-500/30'   },
  closed_won:        { color: 'bg-emerald-600', lightBg: 'bg-emerald-50 dark:bg-emerald-500/10', border: 'border-emerald-300 dark:border-emerald-500/40' },
  closed_settled:    { color: 'bg-slate-400',   lightBg: 'bg-slate-50 dark:bg-slate-800/40',   border: 'border-slate-200 dark:border-slate-700'   },
  intake:            { color: 'bg-slate-500',   lightBg: 'bg-slate-50 dark:bg-slate-800/40',   border: 'border-slate-200 dark:border-slate-700'   },
  allocated:         { color: 'bg-blue-500',    lightBg: 'bg-blue-50 dark:bg-blue-500/10',    border: 'border-blue-200 dark:border-blue-500/30'    },
  judgment_reserved: { color: 'bg-violet-500',  lightBg: 'bg-violet-50 dark:bg-violet-500/10',  border: 'border-violet-200 dark:border-violet-500/30'  },
  closed_lost:       { color: 'bg-red-500',     lightBg: 'bg-red-50 dark:bg-red-500/10',     border: 'border-red-200 dark:border-red-500/30'     },
};

const RISK_DOT: Record<string, string> = {
  High:     'bg-red-500',
  Medium:   'bg-amber-500',
  Low:      'bg-emerald-500',
  Critical: 'bg-red-700',
};

export default function MatterPipelinePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const matters = useMattersStore((s) => s.matters);
  const updateMatter = useMattersStore((s) => s.updateMatter);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  useEffect(() => {
    fetchMatters();
    const poll = setInterval(fetchMatters, 300_000);
    const unsub = onMattersUpdate(() => fetchMatters());
    return () => { clearInterval(poll); unsub(); };
  }, [fetchMatters]);

  const [search, setSearch] = useState('');
  const [dragItem, setDragItem] = useState<Matter | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  const chambersMode = useChambersMode();
  const isBarrister = user?.memberType && ['silk', 'junior', 'pupil', 'door_tenant'].includes(user.memberType);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  // Moving a card PATCHes the matter's status, and opening one POSTs a matter. A pupil holds
  // neither `case.update.*` nor `case.create`, so both answered 403 — and the drag went through
  // the OPTIMISTIC `updateMatter`, which announced "moved to <stage>" before the server replied.
  // The board is read-only for a pupil instead; no other member type is affected.
  const canMoveMatters = canUpdateMatter(user?.memberType);
  const canOpenMatter = canCreateMatter(user?.memberType);
  const clerkFullName = user ? `${user.firstName} ${user.lastName}` : '';
  const { managedBarristers } = useClerkWorkspace(isClerk);
  const principal: PrincipalLite | null = user
    ? { name: clerkFullName, role: isClerk ? 'clerk' : 'admin', memberType: user.memberType }
    : null;

  const stagesSource = chambersMode.isSolo && isBarrister ? SOLO_PIPELINE_STAGES : CHAMBERS_PIPELINE_STAGES;
  const ACTIVE_STAGES = stagesSource
    .filter((s) => !s.key.startsWith('closed'))
    .map((s) => ({ ...s, ...STAGE_STYLES[s.key] }));
  const CLOSED_STAGES = stagesSource
    .filter((s) => s.key.startsWith('closed'))
    .map((s) => ({ ...s, ...STAGE_STYLES[s.key] }));

  const basePath = isBarrister ? '/barrister' : isClerk ? '/clerk' : '/admin';

  const filtered = useMemo(() => {
    let data = matters;
    if (isClerk && principal) {
      data = data.filter((m) => canSeeMatter(principal, m, managedBarristers));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.number.toLowerCase().includes(q) ||
          m.barrister.toLowerCase().includes(q),
      );
    }
    return data;
  }, [matters, search, isClerk, principal, managedBarristers]);

  const columns = useMemo(() => {
    const isSoloBoardView = chambersMode.isSolo && isBarrister;
    return ACTIVE_STAGES.map((stage) => ({
      ...stage,
      matters: filtered
        .filter(
          (m) =>
            m.status === stage.key ||
            (isSoloBoardView && stage.key === 'active' && m.status === 'intake'),
        )
        .sort((a, b) => b.createdAt - a.createdAt),
    }));
  }, [filtered, chambersMode.isSolo, isBarrister]);

  const closedCounts = useMemo(() => {
    return CLOSED_STAGES.map((stage) => ({
      ...stage,
      count: filtered.filter((m) => m.status === stage.key).length,
      totalFees: filtered
        .filter((m) => m.status === stage.key)
        .reduce((s, m) => s + m.feeEstimate, 0),
    }));
  }, [filtered]);

  const totalValue = filtered.reduce((s, m) => s + m.feeEstimate, 0);

  const clerkOpsQ = useQuery({
    queryKey: ['clerk-operations'],
    queryFn: fetchClerkOperations,
    staleTime: 120_000,
    enabled: !isBarrister,
  });

  const healthQ = useQuery({
    queryKey: ['matter-health-30'],
    queryFn: () => fetchMatterHealth(30),
    staleTime: 120_000,
  });

  const healthMap = useMemo(() => {
    const map: Record<string, 'stalled' | 'at_risk' | 'inactive'> = {};
    for (const item of healthQ.data ?? []) map[item.caseId] = item.healthFlag;
    return map;
  }, [healthQ.data]);

  // All four drag handlers no-op for a role that cannot move a matter, so even a synthetic or
  // keyboard-initiated drag cannot reach `updateMatter`. The `draggable` attribute and the drop
  // targets below are withheld as well — this is the belt to that braces.
  const handleDragStart = (m: Matter) => { if (canMoveMatters) setDragItem(m); };
  const handleDragOver = (e: React.DragEvent, stageKey: string) => {
    if (!canMoveMatters) return;
    e.preventDefault();
    setDragOverCol(stageKey);
  };
  const handleDragLeave = () => setDragOverCol(null);
  const handleDrop = (stageKey: string) => {
    if (!canMoveMatters) return;
    if (dragItem && dragItem.status !== stageKey) {
      const stageLabel = PIPELINE_STAGES.find((s) => s.key === stageKey)?.label ?? stageKey;
      const userName = user ? `${user.firstName} ${user.lastName}` : 'System';
      // Confirm the move only once the server has taken it. Dragging to a closed column is one
      // of the ways a matter is CLOSED, so a premature "moved to Closed Settled" could tell a
      // barrister a matter had ended when the server had refused and the card had snapped back.
      const moved = dragItem;
      void updateMatter(moved.id, { status: stageKey, updatedBy: userName, updatedAt: Date.now() })
        .then((ok) => { if (ok) toast.success(`"${moved.name}" moved to ${stageLabel}`); });
    }
    setDragItem(null);
    setDragOverCol(null);
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title={chambersMode.isSolo && isBarrister ? 'My Pipeline' : 'Matter Pipeline'}
        subtitle={
          chambersMode.isSolo && isBarrister
            ? `${filtered.length} matter${filtered.length !== 1 ? 's' : ''} · £${(totalValue / 1000).toFixed(0)}k value`
            : `${filtered.length} matters · £${(totalValue / 1000).toFixed(0)}k total value`
        }
        actions={
          <>
            <Button
              variant="outline"
              size="md"
              leftIcon={<ArrowsLeftRight className="h-4 w-4" />}
              onClick={() => navigate(`${basePath}/matters`)}
            >
              List view
            </Button>
            {canOpenMatter && (
              <Button
                variant="primary"
                size="md"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => navigate(`${basePath}/matters/new`)}
              >
                New matter
              </Button>
            )}
          </>
        }
      />

      {/* The operations strip renders only when its fetch succeeded, and the health badges only
          where a flag arrived. A failed fetch therefore removed the whole strip and every
          stalled / at-risk badge — a board of matters that all looked fine. */}
      {(clerkOpsQ.isError || healthQ.isError) && (
        <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>
            Couldn't load {[clerkOpsQ.isError && !isBarrister && 'the operations summary', healthQ.isError && 'the matter health flags'].filter(Boolean).join(' or ')} —
            a card without a flag below has not been checked, it is not confirmed healthy.
          </span>
          <Button variant="outline" size="sm" onClick={() => { if (clerkOpsQ.isError) void clerkOpsQ.refetch(); if (healthQ.isError) void healthQ.refetch(); }}>
            Try again
          </Button>
        </div>
      )}

      {/* Operational summary bar — clerk/admin only */}
      {!isBarrister && clerkOpsQ.data && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-xs">
          <span className="font-semibold text-foreground">Operations:</span>
          <OperationalChip
            count={clerkOpsQ.data.pendingAllocations}
            label="pending briefs"
            urgent={clerkOpsQ.data.pendingAllocations > 3}
            onClick={() => navigate(`${basePath}/matters`)}
          />
          <OperationalChip
            count={clerkOpsQ.data.unassignedMatters}
            label="unassigned"
            urgent={clerkOpsQ.data.unassignedMatters > 0}
            onClick={() => navigate(`${basePath}/matters`)}
          />
          <OperationalChip
            count={clerkOpsQ.data.pendingConferences}
            label="pending conferences"
            urgent={clerkOpsQ.data.pendingConferences > 2}
            onClick={() => navigate(`${basePath}/matters`)}
          />
          {(healthQ.data ?? []).length > 0 && (
            <OperationalChip
              count={(healthQ.data ?? []).length}
              label="stalled matters"
              urgent={(healthQ.data ?? []).length > 0}
              onClick={() => navigate(`${basePath}/reports`)}
            />
          )}
        </div>
      )}

      {/* Toolbar — borderless, integrated. The search also matches a barrister's name, which is
          what the removed "All barristers" select did. */}
      <div className="mb-5">
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3">
          <Search className="h-4 w-4 text-muted-foreground/60" />
          <input
            type="search"
            placeholder={
              chambersMode.isSolo && isBarrister
                ? 'Filter by name or reference…'
                : 'Filter by name, number, barrister…'
            }
            className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground/50 focus:outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Pipeline board — a grid, not a horizontally scrolling rail.
          Six fixed 260px columns needed 1,580px, so on anything short of a wide monitor most
          of the pipeline sat off-screen behind a sideways scroll. Stages flow into as many
          columns as the screen can hold, so every stage is reachable without scrolling.

          ONE stage per row on a phone, not two. At two columns each stage was ~150px wide, so
          every matter name and barrister name was cut short ("Re Harrington I…", "James
          Thornto…") — and because stages hold wildly different numbers of matters, a 9-card
          Active column sat beside an empty Allocated one, leaving the board a ragged patchwork
          of boxes and gaps. Full width gives each card room for its real name and makes the
          board read top to bottom in pipeline order. */}
      <div className="grid grid-cols-1 gap-2.5 pb-4 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-6">
        {columns.map((col) => (
          <div
            key={col.key}
            className={`flex min-w-0 flex-col rounded-xl transition-colors ${
              dragOverCol === col.key
                ? 'bg-brand-50/60 ring-2 ring-inset ring-brand-200'
                : 'bg-muted/40'
            }`}
            onDragOver={canMoveMatters ? (e) => handleDragOver(e, col.key) : undefined}
            onDragLeave={canMoveMatters ? handleDragLeave : undefined}
            onDrop={canMoveMatters ? () => handleDrop(col.key) : undefined}
          >
            {/* Column header — muted label, count plain */}
            <div className="flex items-center justify-between gap-1 px-3 pb-2 pt-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${col.color}`} />
                <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70 sm:tracking-widest">
                  {col.label}
                </span>
              </div>
              <span className="shrink-0 text-[10px] font-medium text-muted-foreground/50">
                {col.matters.length}
              </span>
            </div>

            {/* Cards */}
            {/* min-h only from sm. Below it a column is a full-width row and a drop-target
                floor just adds empty space — an empty stage is a thin header strip instead. */}
            <div className={`flex-1 space-y-2 p-2.5 ${col.matters.length === 0 ? 'sm:min-h-[72px]' : 'sm:min-h-[120px]'}`}>
              {col.matters.map((m) => {
                const health = healthMap[m.id];
                return (
                  <div
                    key={m.id}
                    draggable={canMoveMatters}
                    onDragStart={canMoveMatters ? () => handleDragStart(m) : undefined}
                    onClick={() => navigate(`${basePath}/matters/${m.id}`)}
                    className={`group cursor-pointer rounded-lg bg-card p-3 shadow-sm transition-all hover:shadow-md ${
                      dragItem?.id === m.id
                        ? 'opacity-40 ring-2 ring-brand-400'
                        : health === 'stalled'
                          ? 'border-l-2 border-l-amber-400'
                          : health === 'at_risk'
                            ? 'border-l-2 border-l-red-400'
                            : ''
                    }`}
                  >
                    {/* Drag handle + case name */}
                    <div className="flex items-start gap-1.5">
                      <DotsSixVertical
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100"
                        weight="bold"
                      />
                      <div className="min-w-0 flex-1">
                        {/* Two lines on a phone, one from sm up. A long case name — "Re
                            Harrington Industrial Holdings Ltd (in administration)" — still
                            overruns a full-width phone card, and a name cut to "Re Harrington
                            Industrial Holdings Ltd (in admin…" tells the user less than the
                            second line does. Narrow desktop columns keep the single line. */}
                        <div className="line-clamp-2 text-sm font-semibold leading-snug text-foreground sm:line-clamp-1">
                          {m.name}
                        </div>
                        <div className="mt-0.5 text-[10px] tracking-wide text-muted-foreground/60">
                          {m.number}
                        </div>
                      </div>
                      {m.riskRating && (
                        <span
                          className={`mt-1 h-2 w-2 shrink-0 rounded-full ${RISK_DOT[m.riskRating] ?? 'bg-slate-300'}`}
                          title={`Risk: ${m.riskRating}`}
                        />
                      )}
                    </div>

                    {/* Access badge — only when restricted or private */}
                    {(m.access === 'private' || m.access === 'restricted') && (
                      <div className="mt-2">
                        {m.access === 'private' && (
                          <Badge variant="danger" className="text-[9px]">Private</Badge>
                        )}
                        {m.access === 'restricted' && (
                          <Badge variant="warning" className="text-[9px]">Restricted</Badge>
                        )}
                      </div>
                    )}

                    {/* Barrister + fee row */}
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        {!(chambersMode.isSolo && isBarrister) && (
                          m.barrister ? (
                            <>
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[8px] font-bold text-brand-700 dark:text-brand-300">
                                {m.barrister.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                              </span>
                              <span className="truncate text-xs text-muted-foreground" title={m.barrister}>{m.barrister}</span>
                            </>
                          ) : (
                            <span className="text-xs italic text-muted-foreground/50">Unassigned</span>
                          )
                        )}
                      </div>
                      {m.feeEstimate > 0 && (
                        <span className="shrink-0 text-[10px] font-semibold tabular-nums text-muted-foreground">
                          £{m.feeEstimate >= 1000
                            ? `${(m.feeEstimate / 1000).toFixed(0)}k`
                            : m.feeEstimate.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Next hearing */}
                    {m.next && m.next !== '—' && (
                      <div className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground/60">
                        <CalendarBlank className="h-3 w-3" />
                        {m.next}
                      </div>
                    )}

                    {/* Health flag */}
                    {health && (
                      <div
                        className={`mt-1.5 text-[10px] font-medium ${
                          health === 'stalled' ? 'text-amber-500' : 'text-red-500'
                        }`}
                      >
                        {health === 'stalled' ? 'Stalled' : 'At risk'}
                      </div>
                    )}
                  </div>
                );
              })}

              {col.matters.length === 0 && (
                <div className="flex h-5 items-center justify-center rounded-lg text-[10px] text-muted-foreground/40 sm:h-20 sm:text-xs">
                  None
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Closed matters summary */}
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {closedCounts.map((c) => (
          <Card
            key={c.key}
            className="flex cursor-pointer items-center justify-between p-4 transition hover:border-brand-300"
            onClick={() => navigate(`${basePath}/matters?status=${c.key.replace('closed_', '')}`)}
          >
            <div className="flex items-center gap-3">
              <span className={`h-2.5 w-2.5 rounded-full ${c.color}`} />
              <div>
                <div className="text-sm font-semibold text-foreground">{c.label}</div>
                <div className="text-xs text-muted-foreground">
                  {c.count} matter{c.count !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-foreground">
                £{(c.totalFees / 1000).toFixed(0)}k
              </div>
              <CaretRight className="h-3.5 w-3.5 text-muted-foreground/40" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function OperationalChip({
  count,
  label,
  urgent,
  onClick,
}: {
  count: number;
  label: string;
  urgent: boolean;
  onClick: () => void;
}) {
  if (count === 0) return <span className="text-muted-foreground">{count} {label}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium transition hover:opacity-80 ${
        urgent
          ? 'border-amber-300 dark:border-amber-500/40 bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'
          : 'border-border bg-card text-foreground'
      }`}
    >
      {urgent && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
      {count} {label}
    </button>
  );
}
